import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import Input from 'components/lib/Input';

import { SHOW_RECIPIENTS_OPTIONS } from '../../../constants';
import {
  CommunitiesHeader,
  CommunitiesTitle,
  CommunityRow,
  EmptyMessage,
  SideColumn,
  StyledCheckbox,
  StyledDisplayFilter,
} from '../CmiDigestForm';
import { CmiDigestUserCommunities } from '../types';

const UserGroupHeader = styled.div`
  background-color: #ecedee;
  font-size: 12px;
  font-weight: 900;
  padding: 6px 8px;
  margin-top: 8px;
`;

const CommunitiesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
`;

type Props = {
  usersCommunities: CmiDigestUserCommunities[];
  baselineCommunityIds: (userId: number) => number[];
  communitiesByUser: Record<number, number[]>;
  setCommunitiesByUser: Dispatch<SetStateAction<Record<number, number[]>>>;
  touchedUserIds: Set<number>;
  setTouchedUserIds: Dispatch<SetStateAction<Set<number>>>;
  disabled: boolean;
};

const CmiDigestBulkCommunitiesPicker = ({
  usersCommunities,
  baselineCommunityIds,
  communitiesByUser,
  setCommunitiesByUser,
  touchedUserIds,
  setTouchedUserIds,
  disabled,
}: Props) => {
  const [search, setSearch] = useState('');
  const [displayFilter, setDisplayFilter] = useState<number>(SHOW_RECIPIENTS_OPTIONS[0].value as number);

  const selectedCommunityIds = (userId: number) => communitiesByUser[userId] ?? baselineCommunityIds(userId);

  const toggleUserCommunity = (userId: number, communityId: number, checked: boolean) => {
    setTouchedUserIds((prev) => new Set(prev).add(userId));
    setCommunitiesByUser((prev) => {
      const current = prev[userId] ?? baselineCommunityIds(userId);
      const next = checked
        ? Array.from(new Set([...current, communityId]))
        : current.filter((id) => id !== communityId);
      return { ...prev, [userId]: next };
    });
  };

  const filteredGroups = useMemo(() => {
    return usersCommunities
      .map((group) => {
        const selectedIds = selectedCommunityIds(group.user.id);
        const communities = group.communities.filter((community) => {
          const matchesSearch = community.comunity_name.toLowerCase().includes(search.toLowerCase());
          const matchesFilter = displayFilter === 0 || selectedIds.includes(community.id);
          return matchesSearch && matchesFilter;
        });
        return { ...group, communities };
      })
      .filter((group) => group.communities.length > 0);
  }, [usersCommunities, search, displayFilter, communitiesByUser]);

  const allFilteredSelected =
    filteredGroups.length > 0 &&
    filteredGroups.every((group) => group.communities.every((c) => selectedCommunityIds(group.user.id).includes(c.id)));

  const toggleSelectAll = (checked: boolean) => {
    setTouchedUserIds((prev) => {
      const next = new Set(prev);
      filteredGroups.forEach((group) => next.add(group.user.id));
      return next;
    });
    setCommunitiesByUser((prev) => {
      const next = { ...prev };
      filteredGroups.forEach((group) => {
        const current = next[group.user.id] ?? baselineCommunityIds(group.user.id);
        const groupCommunityIds = group.communities.map((c) => c.id);
        next[group.user.id] = checked
          ? Array.from(new Set([...current, ...groupCommunityIds]))
          : current.filter((id) => !groupCommunityIds.includes(id));
      });
      return next;
    });
  };

  return (
    <SideColumn className="no-border">
      <StyledDisplayFilter
        options={SHOW_RECIPIENTS_OPTIONS}
        defaultValue={displayFilter}
        onSelect={setDisplayFilter}
        placeholder="Display"
        dataTestId="cmi-digest-bulk-communities-display-filter"
      />

      <CommunitiesHeader>
        <StyledCheckbox
          className="green"
          checked={allFilteredSelected}
          disabled={disabled}
          onChange={(e: any) => toggleSelectAll(e.target.checked)}
          data-test-id="cmi-digest-bulk-communities-select-all"
        >
          <CommunitiesTitle>Communities included</CommunitiesTitle>
        </StyledCheckbox>
      </CommunitiesHeader>

      <Input
        prefix={<SearchOutlined />}
        placeholder="Search communities"
        value={search}
        onChange={(e: any) => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
        data-test-id="cmi-digest-bulk-communities-search-input"
      />

      <CommunitiesListContainer className="mini-scroll">
        {filteredGroups.length === 0 ? (
          <EmptyMessage>No communities found</EmptyMessage>
        ) : (
          filteredGroups.map((group) => (
            <div key={group.user.id}>
              <UserGroupHeader>{group.user.name}</UserGroupHeader>
              {group.communities.map((community) => (
                <CommunityRow key={community.id}>
                  <StyledCheckbox
                    className="green"
                    checked={selectedCommunityIds(group.user.id).includes(community.id)}
                    disabled={disabled}
                    onChange={(e: any) => toggleUserCommunity(group.user.id, community.id, e.target.checked)}
                    data-test-id={`cmi-digest-bulk-community-checkbox-${group.user.id}-${community.id}`}
                  >
                    {community.comunity_name}
                  </StyledCheckbox>
                </CommunityRow>
              ))}
            </div>
          ))
        )}
      </CommunitiesListContainer>
    </SideColumn>
  );
};

export default CmiDigestBulkCommunitiesPicker;
